/**
 * Pure types/helpers for the patient-activities calendar — kept in a module of their own
 * (no component/apiClient imports) so they can be unit-tested without pulling in
 * PatientActivitiesCalendarNew's transitive apiClient/axios chain, the same separation
 * already used by ActivityDetailDialogNew/activityDetailUtils.ts.
 */

/**
 * Shape returned by GET psp-backend/patients-activities/ (see
 * mock-server/psp-backend/patients-activities/get.js). Carries both
 * `plannedDate` (used by ActivityDetailDialogNew) and `startDate`/`endDate`
 * (used by the calendar as its start/end accessors — the same fields the
 * legacy ActivitiesAgenda read directly off the raw activity documents).
 */
export interface PatientActivityRow {
  _id: string
  title?: string
  isVerificationRequired?: boolean
  activityReport?: {
    activityDurationMinutes?: number
    distanceKms?: number
    report?: string
  }
  state?: string
  beneficiary?: {
    lastName?: string
    firstName?: string
    email?: string
    state?: string
  }
  expectedMinuteDuration?: number
  typeCode?: string
  isVisibleOnExternalTouchpoints?: boolean
  plannedDate?: string
  place?: {
    address?: string
    city?: string
    postalCode?: string
    region?: string
    province?: string
    country?: string
    streetNumber?: string
    type?: string
  }
  startDate?: string
  endDate?: string
  [key: string]: any
}

export function getBeneficiaryName(row: PatientActivityRow): string {
  const name = `${row.beneficiary?.lastName ?? ''} ${row.beneficiary?.firstName ?? ''}`.trim()
  return name || '—'
}

// The endpoint (GET psp-backend/patients-activities/) returns every activity in the
// requested date range with no inherent ordering — sort chronologically, oldest first,
// same as the legacy calendar view (ActivitiesAgenda) presented them.
export function sortByPlannedDate(rows: PatientActivityRow[]): PatientActivityRow[] {
  return [...rows].sort((a, b) => {
    const timeA = a.plannedDate ? new Date(a.plannedDate).getTime() : 0
    const timeB = b.plannedDate ? new Date(b.plannedDate).getTime() : 0
    return timeA - timeB
  })
}
